import React, { useEffect, useState } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import ChevronTopIcon from '../../../../assets/icons/ChevronTopIcon';
import { colorsPalette, useIsWidthUp } from '../../../../theme';
import { DashVideo } from '../../../../types';
import { Test } from '../../const';
import { PathwayTestInformation } from '../PathwayTestInformation';
import { ProgressItem } from '../ProgressItem';
import useStyles from './styles';

interface PathwayProgressProps {
  wrapperClass?: string;
  videos: DashVideo[];
  tests: Test[];
  hasSubscription: boolean;
}

export const PathwayProgress: React.FC<PathwayProgressProps> = ({
  wrapperClass = '',
  videos,
  hasSubscription,
  tests,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = !useIsWidthUp('md');
  const [accordionOpened, setAccordionOpened] = useState(!isMobile);

  const watchedVideos = videos.filter((item) => !!item.completed).length;
  const safeValue = watchedVideos ? (watchedVideos / videos.length) * 100 : 0;

  const toggle = () => {
    if (isMobile) {
      setAccordionOpened((prevState) => !prevState);
    }
  };

  useEffect(() => {
    setAccordionOpened(!isMobile);
  }, [isMobile]);

  return (
    <Box className={cx(classes.wrapper, wrapperClass)}>
      <Accordion
        expanded={accordionOpened}
        onChange={toggle}
        classes={{
          root: classes.accordionRoot,
          disabled: classes.accordionDisabled,
        }}
        disabled={!isMobile}
      >
        <AccordionSummary
          classes={{
            root: classes.summaryRoot,
            content: classes.summaryContent,
            expanded: classes.summaryExpanded,
            disabled: classes.summaryDisabled,
          }}
        >
          <Box className={classes.infoWrapper}>
            <Typography data-testid="progressTitle" className={classes.title} variant="h4">
              {t('page.pathway.progress')}
            </Typography>
            <ChevronTopIcon className={classes.expandIcon} color={colorsPalette.white} />
          </Box>
          <Typography data-testid="progressSubtitle" className={classes.subtitle}>
            {t('page.pathway.progressStat', {
              watched: watchedVideos,
              total: videos.length,
            })}
          </Typography>
          {isMobile && !accordionOpened && (
            <div className={cx(classes.progressWrap, 'progress_line')}>
              <div
                className={classes.progress}
                style={{
                  width: `${safeValue}%`,
                }}
              />
            </div>
          )}
        </AccordionSummary>
        <AccordionDetails
          classes={{
            root: classes.detailsRoot,
          }}
        >
          {videos && videos.length > 0 && (
            <ul className={classes.progressList}>
              {videos.map((item, i) => (
                <ProgressItem
                  key={item?.video?.id}
                  id={item?.video?.id}
                  title={item?.video?.title}
                  active={!!item?.completed}
                  nextActive={i < videos.length - 1 ? !!videos[i + 1]?.completed : false}
                />
              ))}
            </ul>
          )}
          {hasSubscription && tests.length > 0 && <PathwayTestInformation tests={tests} />}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PathwayProgress;
